/**
 * Shared viewer styles for tool panels and components.
 * Extracted from tool components for consistency and reuse.
 */

// Common color constants
export const COLORS = {
  white: '#fff',
  black: '#000',
  darkBg: 'rgba(20,20,30,0.6)',
  accentBlue: '#2196F3',
  accentBlueDim: 'rgba(33,150,243,0.3)',
  borderLight: 'rgba(255,255,255,0.1)',
  panelBorder: 'rgba(255,255,255,0.08)',
  panelShadow: '0 8px 32px rgba(0,0,0,0.3)',
  hoverLight: 'rgba(255,255,255,0.1)',
  selectedLight: 'rgba(255,255,255,0.3)',
  highlightedBg: 'rgba(255, 255, 255, 0.3)',
  highlightedBgHover: 'rgba(255, 255, 255, 0.4)',
  itemBg: 'rgba(255,255,255,0.05)',
  itemBgHover: 'rgba(255,255,255,0.1)',
  disabledText: 'rgba(255,255,255,0.3)',
  disabledSlider: 'rgba(255,255,255,0.3)',
  menuBg: '#333',
  tipBg: 'rgba(255,255,255,0.1)',
  deleteHover: 'rgba(255,100,100,0.3)',
  deleteHoverLight: 'rgba(255, 100, 100, 0.2)',
  successHover: 'rgba(100, 255, 100, 0.2)',
  buttonHover: 'rgba(182, 182, 182, 0.2)',
};

// Panel container styles
export const PANEL_STYLES = {
  container: (width = 285) => ({
    position: 'absolute',
    top: 68,
    left: 12,
    zIndex: 1100,
    bgcolor: COLORS.darkBg,
    borderRadius: '12px',
    backdropFilter: 'blur(16px)',
    border: `1px solid ${COLORS.panelBorder}`,
    boxShadow: COLORS.panelShadow,
    width,
    maxHeight: 'calc(100vh - 80px)',
    overflow: 'hidden',
    pl: 1,
  }),
  containerNoPadding: (width = 285) => ({
    position: 'absolute',
    top: 68,
    left: 12,
    zIndex: 1100,
    bgcolor: COLORS.darkBg,
    borderRadius: '12px',
    backdropFilter: 'blur(16px)',
    border: `1px solid ${COLORS.panelBorder}`,
    boxShadow: COLORS.panelShadow,
    width,
    maxHeight: 'calc(100vh - 80px)',
    overflow: 'hidden',
  }),
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    py: 0.5,
    px: 1.5,
    borderBottom: `1px solid ${COLORS.borderLight}`,
  },
  headerTitle: {
    color: COLORS.white,
    fontSize: '0.95rem',
    fontWeight: 600,
  },
  closeButton: {
    color: COLORS.white,
  },
  content: (padding = 1.5) => ({
    p: padding,
  }),
  contentWithScroll: (maxHeight = 'calc(100vh - 140px)') => ({
    p: 1.5,
    maxHeight,
    overflow: 'auto',
  }),
};

// Typography styles
export const TYPOGRAPHY_STYLES = {
  title: {
    color: COLORS.white,
    fontSize: '0.95rem',
    fontWeight: 600,
  },
  subtitle: {
    color: COLORS.white,
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  subtitleSmall: {
    color: COLORS.white,
    fontSize: '0.75rem',
  },
  caption: {
    color: COLORS.white,
    fontSize: '0.75rem',
  },
  captionSmall: {
    color: COLORS.white,
    fontSize: '0.7rem',
  },
  captionWithLabel: {
    color: COLORS.white,
    minWidth: '80px',
    mr: 1.5,
    fontSize: '0.75rem',
  },
  label: {
    color: COLORS.white,
    fontSize: '0.65rem',
    lineHeight: 1.2,
    mt: 0.5,
    height: '2.4em',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
};

// Button styles
export const BUTTON_STYLES = {
  iconButton: {
    color: COLORS.white,
    minWidth: '28px',
    width: '28px',
    height: '28px',
    padding: '4px',
    borderRadius: '8px',
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
    },
    '&.Mui-selected': {
      color: COLORS.white,
      backgroundColor: COLORS.selectedLight,
    },
    '&:hover': {
      backgroundColor: COLORS.hoverLight,
    },
  },
  deleteButton: {
    color: COLORS.white,
    minWidth: '28px',
    width: '28px',
    height: '28px',
    padding: '4px',
    borderRadius: '8px',
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
    },
    '&:hover': {
      backgroundColor: COLORS.deleteHover,
    },
  },
  toggleButton: {
    color: COLORS.white,
    minWidth: '84px',
    height: '28px',
    padding: '2px 8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
    },
    '&.Mui-selected': {
      color: COLORS.white,
      backgroundColor: COLORS.selectedLight,
    },
    '&:hover': {
      backgroundColor: COLORS.hoverLight,
    },
  },
  toolbarIcon: (isOpen) => ({
    bgcolor: isOpen ? COLORS.selectedLight : COLORS.darkBg,
    color: COLORS.white,
    borderRadius: '8px',
    transform: isOpen ? 'scale(1.1)' : 'scale(1)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      bgcolor: isOpen ? COLORS.highlightedBgHover : 'rgba(20,20,30,0.9)',
      transform: 'scale(1.1)',
    },
  }),
  smallIconButton: (isVisible, isHighlighted = false) => ({
    color: isHighlighted ? COLORS.black : COLORS.white,
    p: 0.5,
    '&:hover': {
      bgcolor: COLORS.buttonHover,
    },
    ...(isVisible === false && {
      color: COLORS.disabledText,
      '&.Mui-disabled': {
        color: COLORS.disabledText,
      },
    }),
  }),
  smallDeleteButton: (isHighlighted = false) => ({
    color: isHighlighted ? COLORS.black : COLORS.white,
    p: 0.5,
    '&:hover': {
      bgcolor: COLORS.deleteHoverLight,
    },
  }),
};

// List item styles
export const LIST_STYLES = {
  item: (isHighlighted = false) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    p: 0.75,
    mb: 0.5,
    bgcolor: isHighlighted ? COLORS.highlightedBg : COLORS.itemBg,
    borderRadius: '8px',
    border: isHighlighted ? '2px solid rgba(255, 255, 255, 0.8)' : `1px solid ${COLORS.borderLight}`,
    cursor: 'pointer',
    '&:hover': {
      bgcolor: isHighlighted ? COLORS.highlightedBgHover : COLORS.itemBgHover,
    },
  }),
  scrollContainer: (maxHeight = '400px') => ({
    maxHeight,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: COLORS.borderLight,
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255,255,255,0.3)',
      borderRadius: '2px',
    },
  }),
};

// Form element styles
export const FORM_STYLES = {
  checkbox: {
    color: COLORS.white,
    '&.Mui-checked': { color: COLORS.white },
    ml: 'auto',
  },
  switch: {
    '& .MuiSwitch-switchBase.Mui-checked': { color: COLORS.white },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,0.7)' },
    '& .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,0.3)' },
    '& .MuiSwitch-thumb': { backgroundColor: COLORS.white },
  },
  select: {
    '& .MuiSelect-select': {
      color: COLORS.white,
      fontSize: '0.75rem',
      py: 0.5,
      borderRadius: '6px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
      borderRadius: '6px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.white,
    },
    '& .MuiSvgIcon-root': {
      color: COLORS.white,
    },
  },
  selectMenuProps: {
    PaperProps: {
      sx: {
        bgcolor: COLORS.menuBg,
        color: COLORS.white,
        '& .MuiMenuItem-root': {
          '&:hover': {
            bgcolor: COLORS.hoverLight,
          },
          '&.Mui-selected': {
            bgcolor: 'rgba(255, 255, 255, 0.2)',
          },
        },
      },
    },
  },
  radio: {
    color: COLORS.white,
    '&.Mui-checked': { color: COLORS.white },
    py: 0.5,
  },
};

// Slider styles
export const SLIDER_STYLES = {
  base: {
    flex: 1,
    color: COLORS.white,
    '& .MuiSlider-thumb': { border: '2px solid currentColor' },
    '& .MuiSlider-track': { backgroundColor: 'currentColor' },
    '& .MuiSlider-rail': { opacity: 0.3, backgroundColor: 'currentColor' },
  },
  withLabel: {
    flex: 1,
    color: COLORS.white,
    pr: 1,
    '& .MuiSlider-thumb': { border: '2px solid currentColor' },
    '& .MuiSlider-track': { backgroundColor: 'currentColor' },
    '& .MuiSlider-rail': { opacity: 0.3, backgroundColor: 'currentColor' },
    '& .MuiSlider-valueLabel': {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      color: COLORS.white,
    },
  },
  disableable: {
    flex: 1,
    color: COLORS.white,
    '& .MuiSlider-thumb': { border: '2px solid currentColor' },
    '& .MuiSlider-track': { backgroundColor: 'currentColor' },
    '& .MuiSlider-rail': { opacity: 0.3, backgroundColor: 'currentColor' },
    '&.Mui-disabled': {
      color: COLORS.disabledSlider,
    },
  },
  small: {
    flex: 1,
    color: COLORS.white,
    '& .MuiSlider-thumb': { width: 10, height: 10 },
    '& .MuiSlider-rail': { height: 2 },
    '& .MuiSlider-track': { height: 2 },
  },
};

// Expandable section styles
export const SECTION_STYLES = {
  expandableHeader: {
    display: 'flex',
    alignItems: 'center',
    mb: 0.5,
    cursor: 'pointer',
  },
  expandIcon: (isExpanded) => ({
    color: COLORS.white,
    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.3s ease',
  }),
  tipBox: {
    p: 1,
    bgcolor: COLORS.tipBg,
    borderRadius: 1,
  },
  emptyState: {
    p: 2,
    textAlign: 'center',
    bgcolor: COLORS.itemBg,
    borderRadius: 1,
    border: `1px solid ${COLORS.borderLight}`,
    mb: 0.5,
  },
};

// Layer selection styles (for LayersTools)
export const LAYER_STYLES = {
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 1,
  },
  item: {
    cursor: 'pointer',
    textAlign: 'center',
  },
  thumbnail: (isSelected, iconUrl, fallbackBgColor = '#444') => ({
    width: '80%',
    height: 60,
    borderRadius: 2,
    border: isSelected ? '3px solid #fff' : '2px solid transparent',
    overflow: 'hidden',
    mb: 1,
    position: 'relative',
    margin: '0 auto',
    backgroundImage: iconUrl ? `url(${iconUrl})` : undefined,
    backgroundSize: iconUrl ? 'cover' : undefined,
    backgroundPosition: iconUrl ? 'center center' : undefined,
    backgroundRepeat: iconUrl ? 'no-repeat' : undefined,
    backgroundClip: 'padding-box',
    backgroundColor: fallbackBgColor,
    '&:hover': {
      border: isSelected ? '3px solid #fff' : '1px solid rgba(255,255,255,0.7)',
    },
  }),
  placeholder: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '0.7rem',
  },
};

// Tooltip styles (glassmorphism matching panels/toolbar)
export const TOOLTIP_STYLES = {
  tooltip: {
    bgcolor: COLORS.darkBg,
    backdropFilter: 'blur(16px)',
    border: `1px solid ${COLORS.panelBorder}`,
    boxShadow: COLORS.panelShadow,
    borderRadius: '8px',
    p: 1,
    color: COLORS.white,
  },
  arrow: {
    color: COLORS.darkBg,
  },
};

// Row layout styles
export const ROW_STYLES = {
  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    mb: 1,
    mr: 1,
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    mb: 0.5,
  },
  buttonRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    mb: 0.5,
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    mb: 1,
  },
};
